// TelephonyOnboardingWizard.jsx
// A single AppModal-based, config-driven wizard that cross-sells a Twilio phone
// number and the AI Virtual Receptionist BOTH ways:
//   - entryMode "number"       — order a number, then upsell the receptionist.
//   - entryMode "receptionist" — set up the receptionist, then prompt to buy a
//                                number so it has something to answer.
//
// The step ORDER is computed from entryMode + the upsell answer (see
// buildTelephonySteps); both modes reuse the SAME step components. Each step
// receives a shared `wizard` bag (state + setters + dispatch helpers) plus
// `goNext`/`goBack`, and gates the Next button via `canAdvance`. Pattern copied
// from CreateUpdateCustomerModal (AppModal shell + left step rail + right panel).

import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Row, Col, Nav, NavItem, NavLink } from "reactstrap";
import classnames from "classnames";
import { useDispatch } from "react-redux";

import AppModal from "../../Common/AppModal";
import { buildTelephonySteps } from "./telephonyWizardConfig";

export default function TelephonyOnboardingWizard({
  open,
  onClose,
  entryMode = "number",
  onComplete,
  // Change #100774 - "mobile" (UK 07, can text) or "local" (calls only in the UK).
  defaultNumberType = "local",
}) {
  const dispatch = useDispatch();

  // --- Wizard state (shared across steps) ---
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [passedSteps, setPassedSteps] = useState([0]);

  // Number ordering
  const [callProviderId, setCallProviderId] = useState(null);
  const [country, setCountry] = useState("GB"); // ISO alpha-2 for the number search/buy
  const [selectedNumber, setSelectedNumber] = useState(""); // chosen from search
  const [purchasedNumber, setPurchasedNumber] = useState(""); // confirmed after buy
  const [areaCode, setAreaCode] = useState("");
  const [contains, setContains] = useState("");
  const [nickname, setNickname] = useState("");
  const [numberType, setNumberType] = useState(defaultNumberType);

  // Receptionist upsell answer (number mode). null = not yet answered.
  const [wantsReceptionist, setWantsReceptionist] = useState(null);

  // Receptionist config / provisioning
  const [receptionistId, setReceptionistId] = useState(null); // set by create
  const [name, setName] = useState("");
  const [systemPrompt, setSystemPrompt] = useState("");
  const [greeting, setGreeting] = useState("");
  const [voice, setVoice] = useState("");
  const [model, setModel] = useState("");
  const [assistantId, setAssistantId] = useState("");
  const [sipUri, setSipUri] = useState("");
  const [twilioLinked, setTwilioLinked] = useState(false);
  const [receptionistSaved, setReceptionistSaved] = useState(false);
  const [assistantProvisioned, setAssistantProvisioned] = useState(false);

  const steps = useMemo(
    () => buildTelephonySteps(entryMode, wantsReceptionist),
    [entryMode, wantsReceptionist]
  );

  // Keep the active index in range if the step list shrinks (e.g. the user
  // flips the upsell answer from "yes" back to "no").
  useEffect(() => {
    if (activeStepIndex > steps.length - 1) {
      setActiveStepIndex(steps.length - 1);
    }
  }, [steps.length, activeStepIndex]);

  const resetAll = () => {
    setActiveStepIndex(0);
    setPassedSteps([0]);
    setCountry("GB");
    setSelectedNumber("");
    setPurchasedNumber("");
    setAreaCode("");
    setContains("");
    setNickname("");
    setNumberType(defaultNumberType);
    setWantsReceptionist(null);
    setReceptionistId(null);
    setName("");
    setSystemPrompt("");
    setGreeting("");
    setVoice("");
    setModel("");
    setAssistantId("");
    setSipUri("");
    setTwilioLinked(false);
    setReceptionistSaved(false);
    setAssistantProvisioned(false);
  };

  const handleClose = () => {
    resetAll();
    if (onClose) onClose();
  };

  const goToStep = (targetIndex) => {
    if (targetIndex < 0 || targetIndex > steps.length - 1) return;
    setPassedSteps((prev) => [...new Set([...prev, activeStepIndex])]);
    setActiveStepIndex(targetIndex);
  };

  const goNext = () => goToStep(activeStepIndex + 1);
  const goBack = () => goToStep(activeStepIndex - 1);

  const handleDone = () => {
    if (onComplete) onComplete();
    handleClose();
  };

  // The wizard bag handed to every step. Keeps steps thin and reusable.
  const wizard = {
    dispatch,
    entryMode,
    // number ordering
    callProviderId,
    setCallProviderId,
    country,
    setCountry,
    selectedNumber,
    setSelectedNumber,
    purchasedNumber,
    setPurchasedNumber,
    areaCode,
    setAreaCode,
    contains,
    setContains,
    nickname,
    setNickname,
    numberType,
    setNumberType,
    // upsell
    wantsReceptionist,
    setWantsReceptionist,
    // receptionist
    receptionistId,
    setReceptionistId,
    name,
    setName,
    systemPrompt,
    setSystemPrompt,
    greeting,
    setGreeting,
    voice,
    setVoice,
    model,
    setModel,
    assistantId,
    setAssistantId,
    sipUri,
    setSipUri,
    twilioLinked,
    setTwilioLinked,
    receptionistSaved,
    setReceptionistSaved,
    assistantProvisioned,
    setAssistantProvisioned,
    // navigation
    goNext,
    goBack,
    goToStep,
    handleDone,
  };

  const ActiveStep = steps[activeStepIndex] || steps[0];
  const ActiveStepComponent = ActiveStep.component;

  const title =
    entryMode === "receptionist"
      ? "Set up your AI Virtual Receptionist"
      : "Order a phone number";
  const subtitle =
    entryMode === "receptionist"
      ? "Configure the receptionist, then give it a number to answer."
      : "Buy a number and optionally add an AI receptionist to answer it.";

  return (
    <AppModal
      open={open}
      onClose={handleClose}
      title={title}
      subtitle={subtitle}
      icon={<i className="ri-phone-line" />}
      headerAccent="indigo"
      size="lg"
      hideFooter
      contentProps={{ className: "app-modal__content p-0 bg-light" }}
    >
      <Row className="g-0 h-100">
        {/* Left step rail */}
        <Col lg={3} className="bg-white border-end p-4 shadow-sm z-0">
          <Nav className="flex-column custom-nav nav-pills gap-3">
            {steps.map((step, index) => (
              <NavItem key={step.id}>
                <NavLink
                  href="#"
                  className={classnames(
                    "rounded-3 p-3 transition-all border d-flex align-items-center",
                    {
                      "bg-pastel-blue text-pastel-blue border-pastel-blue shadow-sm":
                        activeStepIndex === index,
                      "bg-white text-secondary border-light hover-bg-light":
                        activeStepIndex !== index,
                    }
                  )}
                  onClick={(e) => {
                    e.preventDefault();
                    // Only allow jumping back to a step already visited — the
                    // flow is sequential (search → buy → link), so forward jumps
                    // would skip required actions.
                    if (index <= activeStepIndex || passedSteps.includes(index)) {
                      goToStep(index);
                    }
                  }}
                >
                  <div className="flex-grow-1">
                    <span
                      className={`fw-bold d-block ${
                        activeStepIndex === index ? "text-pastel-blue" : ""
                      }`}
                    >
                      Step {index + 1}
                    </span>
                    <small
                      className={
                        activeStepIndex === index
                          ? "text-pastel-blue"
                          : "text-muted"
                      }
                    >
                      {step.title}
                    </small>
                  </div>
                  <div>
                    {passedSteps.includes(index) &&
                      activeStepIndex !== index && (
                        <i className="fa-solid fa-circle-check text-success fs-5 opacity-75"></i>
                      )}
                  </div>
                </NavLink>
              </NavItem>
            ))}
          </Nav>
        </Col>

        {/* Right content panel */}
        <Col lg={9} className="p-0 bg-light scrollable-content">
          <div className="p-4">
            <ActiveStepComponent wizard={wizard} />
          </div>
        </Col>
      </Row>
    </AppModal>
  );
}

TelephonyOnboardingWizard.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  entryMode: PropTypes.oneOf(["number", "receptionist"]),
  onComplete: PropTypes.func,
  defaultNumberType: PropTypes.oneOf(["local", "mobile"]),
};
